/**
 * The purchase the server describes on an access (LIS-1173,
 * docs: the 1-tap purchase design, §2.3–§2.8): what is for sale and at what
 * price, whether it can be bought, the questions to ask with the buyer's
 * saved answers, and the payment policy. Clients draw it and know no seller.
 * Every union is closed: a kind outside it fails the parse, and both
 * clients switch on every kind exhaustively.
 *
 * Schemas only, with no world data: the readers over them (`readAnswers`,
 * `formFor`, `readDestination`, `orderTotal`) are on the `./purchase`
 * subpath, which reads `countries.json` through `./world`.
 */
import { z } from "zod";
import { cardBrands } from "@libs/utils/card-brands";
import {
  journeyApproxSchema,
  MOST_LISTED_TICKETS,
  storeCurrencySchema,
  vendorDeliveryFeeTiersSchema,
} from "./vendor-checkout";

export {
  cardBrandIdOf,
  cardBrandIds,
  cardBrands,
  cardBrandTaken,
  type CardBrand,
  type CardBrandId,
} from "@libs/utils/card-brands";

/** A country, as ISO 3166-1 alpha-2 in capitals. */
export const purchaseCountrySchema = z.string().regex(/^[A-Z]{2}$/);

const itemSchema = z
  .object({
    /** The selection's `item_id`. */
    id: z.string().min(1).max(100),
    name: z.string().trim().min(1).max(240),
    priceMinor: z.number().int().nonnegative(),
    /** The per-item booking fee. */
    feeMinor: z.number().int().nonnegative(),
    min: z.number().int().min(1),
    /** Null: the seller states no limit. */
    max: z.number().int().min(1).nullable(),
    available: z.boolean(),
    /** The signed-in member's price of one, before its booking fee, where a
     *  club discount code the server chose for them covers the item
     *  (LIS-1178): the sheet shows it with `priceMinor` struck through, and
     *  the total takes the difference off (`orderTotal`). Absent for anyone
     *  else. */
    memberPriceMinor: z.number().int().nonnegative().optional(),
  })
  .strict();
export type PurchaseItem = z.infer<typeof itemSchema>;

const optionItemSchema = itemSchema
  .extend({ values: z.array(z.string().trim().min(1).max(240)).min(1).max(4) })
  .strict();

const optionsPickerSchema = z
  .object({
    kind: z.literal("options"),
    /** What each option is ("Size"), in the order the sheet shows them. */
    names: z.array(z.string().trim().min(1).max(60)).min(1).max(4),
    /** The item each option belongs to: the product's own name, or a bundle
     *  part's ("T-Shirt (TOUR)", "Badge"). */
    parts: z.array(z.string().trim().min(1).max(240)).min(1).max(4),
    items: z.array(optionItemSchema).min(1).max(MOST_LISTED_TICKETS),
  })
  .strict()
  .superRefine((picker, ctx) => {
    const issue = (message: string) => ctx.addIssue({ code: "custom", message });
    if (picker.parts.length !== picker.names.length) issue("one part per option");
    const keys = picker.names.map((name, index) =>
      JSON.stringify([picker.parts[index]?.trim().toLowerCase() ?? "", name.trim().toLowerCase()]),
    );
    if (new Set(keys).size !== keys.length) issue("each option once per part");
    const combinations = new Set<string>();
    for (const item of picker.items) {
      if (item.values.length !== picker.names.length) issue("one value per option");
      combinations.add(JSON.stringify(item.values));
    }
    if (combinations.size !== picker.items.length) issue("each combination once");
    if (!picker.items.some((item) => item.available)) issue("one combination on sale");
  });

/** How the sheet chooses: a row per item, or a product's options. A
 *  product whose stored option data fails today's checks is `rows`. */
export const pickerSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("rows"), items: z.array(itemSchema).min(1).max(MOST_LISTED_TICKETS) }).strict(),
  optionsPickerSchema,
]);
export type Picker = z.infer<typeof pickerSchema>;

/** The delivery of the goods: none (tickets), the listing's own postage
 *  ladder to `country`, the store's own live options for the basket
 *  (`quoted`, asked with the buyer's destination), postage not measured
 *  yet (`unmeasured`: a demo, never free), or a digital download the store
 *  emails (`emailed`: nothing is posted, so no delivery is asked, quoted or
 *  added, and the address is the card's billing address). */
export const purchaseDeliverySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }).strict(),
  z
    .object({
      kind: z.literal("ladder"),
      country: purchaseCountrySchema,
      tiers: vendorDeliveryFeeTiersSchema,
      dutiesDue: z.boolean(),
    })
    .strict(),
  z.object({ kind: z.literal("quoted"), country: purchaseCountrySchema }).strict(),
  z.object({ kind: z.literal("unmeasured") }).strict(),
  z.object({ kind: z.literal("emailed") }).strict(),
]);
export type PurchaseDelivery = z.infer<typeof purchaseDeliverySchema>;

/** Why a listing is shown and never bought: its seller (1-tap buys nothing
 *  from it), its currency, the fan's delivery country, or postage not yet
 *  measured. */
export const purchaseDemoReasons = ["seller", "currency", "country", "postage_unmeasured"] as const;

export const purchaseSaleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("buyable") }).strict(),
  z
    .object({
      kind: z.literal("demo"),
      reason: z.enum(purchaseDemoReasons),
      message: z.string().min(1).max(240),
    })
    .strict(),
]);
export type PurchaseSale = z.infer<typeof purchaseSaleSchema>;

// Fields (§2.4) -------------------------------------------------------------

const fieldBase = {
  id: z.string().min(1).max(60),
  label: z.string().min(1).max(120),
  required: z.boolean(),
  note: z.string().max(400).nullable(),
};

/** What the seller's form does with an address part. */
export const partPolicySchema = z.enum(["required", "optional", "hidden"]);
/** A region: hidden, required by the seller, or as the country has it
 *  (shown where the country has a required region or a list, required
 *  where the country requires one). */
export const regionPolicySchema = z.enum(["hidden", "required", "as_country"]);

/** The wire limit for the sentence beside a refused address country. */
export const COUNTRY_REFUSAL_MAX_LENGTH = 200;

/** A country an address lists: accepted ones use the world's format for
 *  that country; refused ones are shown with the seller's sentence. */
export const listedCountrySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("accepted"), code: purchaseCountrySchema }).strict(),
  z
    .object({ kind: z.literal("refused"), code: purchaseCountrySchema, message: z.string().min(1).max(COUNTRY_REFUSAL_MAX_LENGTH) })
    .strict(),
]);
export type ListedCountry = z.infer<typeof listedCountrySchema>;

const addressText = z.string().max(240).nullable();
/** An address as a form holds it: its country and each part. */
export const addressValueSchema = z
  .object({
    country: purchaseCountrySchema,
    line1: addressText,
    line2: addressText,
    city: addressText,
    region: addressText,
    postcode: addressText,
  })
  .strict();
export type AddressValue = z.infer<typeof addressValueSchema>;

export const textFieldSchema = z
  .object({
    ...fieldBase,
    kind: z.literal("text"),
    input: z.enum(["text", "email", "numeric"]),
    autoComplete: z.string().max(60).nullable(),
    /** How a typed value is read before it is checked: trimmed; an email
     *  trimmed and in lower case; a document number without its spaces,
     *  dots and dashes, in capitals ("12.345.678" is 12345678). */
    normalise: z.enum(["trim", "email", "document"]),
    pattern: z.string().max(400).nullable(),
    message: z.string().max(200).nullable(),
    maxLength: z.number().int().min(1).max(254),
    value: z.string().nullable(),
  })
  .strict();

export const choiceFieldSchema = z
  .object({
    ...fieldBase,
    kind: z.literal("choice"),
    options: z.array(z.object({ value: z.string().min(1).max(60), label: z.string().min(1).max(120) }).strict()).min(1).max(300),
    value: z.string().nullable(),
  })
  .strict();

export const phoneFieldSchema = z
  .object({
    ...fieldBase,
    kind: z.literal("phone"),
    /** Read by `readPhone` (`./world`), in the country it is dialled in. */
    value: z.object({ country: purchaseCountrySchema, number: z.string().max(40) }).strict().nullable(),
  })
  .strict();

export const dateFieldSchema = z
  .object({
    ...fieldBase,
    kind: z.literal("date"),
    /** `day`: an ISO date (YYYY-MM-DD); `year`: four digits. */
    precision: z.enum(["day", "year"]),
    earliest: z.iso.date(),
    latest: z.enum(["today"]),
    value: z.string().nullable(),
  })
  .strict();

const labelledPart = <Policy extends z.ZodType<string>>(policy: Policy) =>
  z.object({ policy, label: z.string().min(1).max(120) }).strict();

export const addressFieldSchema = z
  .object({
    ...fieldBase,
    kind: z.literal("address"),
    parts: z
      .object({
        line1: labelledPart(partPolicySchema),
        line2: labelledPart(z.enum(["optional", "hidden"])),
        city: labelledPart(partPolicySchema),
        region: z.object({ policy: regionPolicySchema }).strict(),
        /** Its noun follows the chosen country ("Card ZIP code"). */
        postcode: labelledPart(partPolicySchema),
      })
      .strict(),
    /** Every country the world module knows, or the listed ones. */
    countries: z.union([z.literal("all_accepted"), z.array(listedCountrySchema).min(1).max(300)]),
    /** The country it starts on where the buyer has none. */
    suggested: purchaseCountrySchema.nullable(),
    /** A change of country reads the access again (merch prices and
     *  delivery follow the destination). */
    reprices: z.boolean(),
    value: addressValueSchema.nullable(),
  })
  .strict();

export const fieldSchema = z.discriminatedUnion("kind", [
  textFieldSchema,
  choiceFieldSchema,
  phoneFieldSchema,
  dateFieldSchema,
  addressFieldSchema,
]);
export type Field = z.infer<typeof fieldSchema>;
export type TextField = z.infer<typeof textFieldSchema>;
export type ChoiceField = z.infer<typeof choiceFieldSchema>;
export type PhoneField = z.infer<typeof phoneFieldSchema>;
export type DateField = z.infer<typeof dateFieldSchema>;
export type AddressField = z.infer<typeof addressFieldSchema>;

/** A group of fields the sheet draws under one heading. */
export const sectionSchema = z
  .object({
    id: z.string().min(1).max(60),
    title: z.string().min(1).max(120),
    /** What the sheet says under the section. */
    note: z.string().max(400).nullable(),
    fields: z.array(fieldSchema).min(1).max(30),
  })
  .strict()
  .refine((section) => new Set(section.fields.map((field) => field.id)).size === section.fields.length, "unique ids");
export type Section = z.infer<typeof sectionSchema>;

// Payment (§2.8) ------------------------------------------------------------

/** Which cards the seller's checkout takes: null, any card; a list, only
 *  those brands (a card of another classified network is refused, an
 *  unclassified one taken). `freeOrders`: a total of 0 needs no card. */
export const paymentPolicySchema = z
  .object({
    freeOrders: z.boolean(),
    brands: z
      .array(z.object({ id: z.enum(cardBrands), name: z.string().min(1).max(60) }).strict())
      .min(1)
      .nullable(),
  })
  .strict();
export type PaymentPolicy = z.infer<typeof paymentPolicySchema>;

// The purchase (§2.3) -------------------------------------------------------

export const purchaseSchema = z
  .object({
    /** The listing the sheet buys from: a start, an estimate and every
     *  answer name it. */
    listingId: z.string().min(1).max(100),
    goods: z.enum(["tickets", "merch"]),
    seller: z.object({ name: z.string().min(1).max(120) }).strict(),
    title: z.string().trim().min(1).max(240),
    startsAt: z.iso.datetime().nullable(),
    currency: storeCurrencySchema,
    /** The estimate in the fan's own currency. */
    approx: journeyApproxSchema.nullable(),
    picker: pickerSchema,
    /** The most units one order holds across its lines; null: each line's
     *  own limit only. */
    maxOrderQuantity: z.number().int().min(1).nullable(),
    /** The flat fee per order, and the basket fee ladder of tickets
     *  (Skiddle's, Fatsoma's, AltTickets'). */
    fees: z
      .object({ orderFeeMinor: z.number().int().nonnegative(), ladder: vendorDeliveryFeeTiersSchema })
      .strict(),
    delivery: purchaseDeliverySchema,
    tax: z.enum(["included", "added_at_checkout"]),
    /** Where its seller adds tax at checkout, the rate it charges where
     *  that is one public rate for the buyer's country (a download's VAT in
     *  the UK), in basis points: the checkout says the tax (`orderTotal`'s
     *  `taxMinor`). Absent: "+ local tax" (LIS-1194). */
    taxRateBps: z.number().int().min(1).max(10_000).optional(),
    /** Where the rate depends on the buyer's state or province (US sales
     *  tax, Canada's provincial taxes), each region's whose rate is known,
     *  by its code ("TX", "ON"): the checkout says the tax once the buyer's
     *  address names one of them (`orderTotal`'s `region`). Absent, and for
     *  any other region: "+ local tax" (LIS-1194). */
    regionTaxRatesBps: z.record(z.string().regex(/^[A-Z0-9]{1,3}$/), z.number().int().min(1).max(10_000)).optional(),
    sale: purchaseSaleSchema,
    form: z.object({ sections: z.array(sectionSchema).min(1).max(4) }).strict(),
    payment: paymentPolicySchema,
  })
  .strict()
  .superRefine((purchase, ctx) => {
    const issue = (message: string) => ctx.addIssue({ code: "custom", message });
    const unmeasured = purchase.delivery.kind === "unmeasured";
    const postage = purchase.sale.kind === "demo" && purchase.sale.reason === "postage_unmeasured";
    if (unmeasured !== postage) issue("unmeasured postage is a demo for its postage");
    if (purchase.delivery.kind === "none" && purchase.goods !== "tickets") issue("goods are delivered");
    if (purchase.taxRateBps !== undefined && purchase.tax !== "added_at_checkout") issue("a tax rate is a tax added at checkout");
    if (purchase.regionTaxRatesBps !== undefined && (purchase.tax !== "added_at_checkout" || purchase.taxRateBps !== undefined))
      issue("a region's tax rate is a tax added at checkout, where the country has none");
    if ((purchase.delivery.kind === "ladder" || purchase.delivery.kind === "quoted") && purchase.goods !== "merch")
      issue("tickets are not posted");
    if (purchase.delivery.kind === "emailed" && purchase.goods !== "merch") issue("only merch is emailed by its store");
    if (purchase.delivery.kind === "ladder" && purchase.delivery.tiers.length && purchase.fees.ladder.length)
      issue("one ladder per listing");
    if (purchase.approx && purchase.approx.currency === purchase.currency) issue("an estimate is in another currency");
    const ids = purchase.form.sections.flatMap((section) => section.fields.map((field) => field.id));
    if (new Set(ids).size !== ids.length) issue("field ids are unique across sections");
    const items = purchase.picker.items.map((item) => item.id);
    if (new Set(items).size !== items.length) issue("item ids are unique");
    if (purchase.picker.items.some((item) => item.memberPriceMinor !== undefined && item.memberPriceMinor >= item.priceMinor))
      issue("a member price is below the price");
  });
export type Purchase = z.infer<typeof purchaseSchema>;

// Answers (§2.5) ------------------------------------------------------------

/** A text, choice or date answer: null only where the field is optional. */
export const valueAnswerSchema = z.object({ value: z.string().max(1000).nullable() }).strict();
export const phoneAnswerSchema = z
  .object({ country: purchaseCountrySchema, number: z.string().max(40) })
  .strict()
  .nullable();
/** A part the chosen country's form hides is absent or null. */
const answeredPart = z.string().max(1000).nullish();
export const addressAnswerSchema = z
  .object({
    country: purchaseCountrySchema,
    line1: answeredPart,
    line2: answeredPart,
    city: answeredPart,
    region: answeredPart,
    postcode: answeredPart,
  })
  .strict();
/** The buyer's answers to a purchase's fields, one per field id. Each is
 *  read by its field's kind (`readAnswers`). */
export const answersSchema = z
  .array(
    z
      .object({
        id: z.string().min(1).max(60),
        answer: z.union([valueAnswerSchema, phoneAnswerSchema, addressAnswerSchema]),
      })
      .strict(),
  )
  .max(60);
export type Answers = z.infer<typeof answersSchema>;

// Delivery estimates (§2.7) -------------------------------------------------

/** Where an estimate is asked to deliver: the country, its postcode read
 *  under the country's rules, and the country's region where it has
 *  regions ("US-NY", or a typed one). Nothing more of the address. */
export const destinationSchema = z
  .object({
    country: purchaseCountrySchema,
    postcode: z.string().max(240).nullable(),
    region: z.string().max(240).nullable(),
  })
  .strict();
export type Destination = z.infer<typeof destinationSchema>;
